import type { PropsWithChildren, ReactNode } from 'react';
import { useOverflowHandler } from '../../../hooks/useOverflowHandler';
import { Box, type BoxProps, HStack } from '../../Core';
import type { TooltipProps } from '../../Tooltip';
import { Tooltip } from '../../Tooltip';

/** This is very similar in nature to the styled-component EllipsisText, but that had limitations getting it to work
 * with the auto-tooltip you ideally want whenever you're ellipsizing text.  This allows that functionality as a built-in behavior.
 *
 * By default, handles horizontal ellipsizing, but by setting lineClamp, this changes to handle vertical ellipsizing based on lines.
 *
 * Once we're confident on this component, we could consider removing EllipsisText and replacing it with this.
 */
export const EllipsisBoxWithTooltip = ({
  children,
  prefixNode: prefixRender,
  suffixNode: suffixRender,
  tooltip,
  tooltipTestId,
  tooltipZIndex,
  tooltipProps,
  outerBoxProps,
  lineClamp,
  alwaysShowTooltip,
  suppressOverflowTooltip,
  ...props
}: PropsWithChildren<
  BoxProps & {
    /** Fixed content rendered before the ellipsized text (e.g. status icon). Does not shrink. (called 'prefixNode' since Box already has prefix) */
    prefixNode?: ReactNode;
    /** Fixed content rendered after the ellipsized text. Does not shrink. (called 'suffixNode' since Box already has suffix) */
    suffixNode?: ReactNode;
    /** Tooltip content override. Defaults to children. */
    tooltip?: ReactNode;
    /** For vertical ellipsizing, supports line-clamping the text to lines
     * - if set, will override the whiteSpace, textOverflow, and overflow properties
     */
    lineClamp?: number;
    /** Box Props for Container Box */
    outerBoxProps?: BoxProps;
    'data-testid'?: string;
    tooltipTestId?: string;
    tooltipZIndex?: number;
    tooltipProps?: Partial<TooltipProps>;
    alwaysShowTooltip?: boolean;
    /** Keep CSS ellipsis but never show the overflow tooltip. */
    suppressOverflowTooltip?: boolean;
  }
>) => {
  const { isOverflowing, ref } = useOverflowHandler({ skip: suppressOverflowTooltip });
  const hasDecorations = prefixRender != null || suffixRender != null;
  const tooltipContent = tooltip ?? children;

  const ellipsisBox = lineClamp ? (
    <Box
      ref={ref}
      lineClamp={lineClamp}
      minWidth={hasDecorations ? 0 : undefined}
      flex={hasDecorations ? '1 1 auto' : undefined}
      {...props}
    >
      {children}
    </Box>
  ) : (
    <Box
      ref={ref}
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      overflow="hidden"
      minWidth={hasDecorations ? 0 : undefined}
      flex={hasDecorations ? '1 1 auto' : undefined}
      {...props}
    >
      {children}
    </Box>
  );

  const tooltipElement = suppressOverflowTooltip ? (
    ellipsisBox
  ) : (
    <Tooltip
      delay={500}
      tooltip={tooltipContent}
      placement="bottom"
      trigger={alwaysShowTooltip || isOverflowing ? 'hover' : ''}
      tooltipTestID={tooltipTestId}
      zIndex={tooltipZIndex}
      {...tooltipProps}
    >
      {ellipsisBox}
    </Tooltip>
  );

  if (!hasDecorations) {
    return (
      <Box maxWidth="100%" overflow="hidden" {...outerBoxProps}>
        {tooltipElement}
      </Box>
    );
  }

  return (
    <Box maxWidth="100%" overflow="hidden" {...outerBoxProps}>
      <HStack alignItems="center" gap="spacing4" minWidth={0}>
        {prefixRender != null ? <Box flex="0 0 auto">{prefixRender}</Box> : null}
        <Box minWidth={0} flex="1 1 auto" overflow="hidden">
          {tooltipElement}
        </Box>
        {suffixRender != null ? <Box flex="0 0 auto">{suffixRender}</Box> : null}
      </HStack>
    </Box>
  );
};
